import { supabase } from '@/integrations/supabase/client';
import { Customer, Bike, Rental, Branch, Plan, ExchangeRate, RentalWithRelations, Shift, ShiftWithRelations, ShiftPayment, Configuration, UserProfile } from '@/types/models';
import { compressImage } from '@/lib/imageCompression';

// Exchange Rates
export const getExchangeRates = async (): Promise<ExchangeRate[]> => {
  const { data, error } = await supabase
    .from('exchange_rates')
    .select('*')
    .order('date', { ascending: false });

  if (error) throw error;
  return data || [];
};

export const getCurrentExchangeRate = async (): Promise<ExchangeRate | null> => {
  const { data, error } = await supabase
    .from('exchange_rates')
    .select('*')
    .order('date', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) throw error;
  return data;
};

export const recalculateShiftTotalsAdmin = async (shiftId: string) => {
  const { data, error } = await supabase.rpc('recalculate_shift_totals_admin', {
    p_shift_id: shiftId,
  });

  if (error) {
    console.error('Error recalculating shift totals:', error);
    throw error;
  }

  return data;
};

export const createExchangeRate = async (exchangeRateData: Omit<ExchangeRate, 'id' | 'created_at' | 'updated_at'>) => {
  const { data, error } = await supabase
    .from('exchange_rates')
    .insert({
      usd_to_bs: exchangeRateData.usd_to_bs,
      date: exchangeRateData.date,
    })
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const updateExchangeRate = async (id: string, exchangeRateData: Partial<ExchangeRate>) => {
  const { data, error } = await supabase
    .from('exchange_rates')
    .update(exchangeRateData)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const getRentalHistoryByCustomer = async (customerId: string): Promise<RentalWithRelations[]> => {
  const { data, error } = await supabase
    .from('rentals')
    .select(`
      *,
      bike:bike_id(id, model, type, color, number)
    `)
    .eq('customer_id', customerId)
    .order('start_date', { ascending: false });

  if (error) throw error;
  return data || [];
};

// Customers
export const createCustomer = async (params: any) => {
  let customerData: Omit<Customer, 'id'>;
  let shouldReturnData = true;

  // Runtime check for backward compatibility.
  // If params has a `customerData` property, we assume the new calling convention.
  if (params && params.customerData) {
    customerData = params.customerData;
    shouldReturnData = params.shouldReturnData ?? true; // Default to true if undefined
  } else {
    // Otherwise, assume the old convention where params is the customerData object itself.
    customerData = params;
  }

  const query = supabase
    .from('customers')
    .insert({
      id_card: customerData.id_card,
      name: customerData.name,
      phone: customerData.phone,
      email: customerData.email ?? null,
      id_photo_url: customerData.id_photo_url ?? null,
      profile_photo_url: customerData.profile_photo_url,
      contract_url: customerData.contract_url ?? null
    });

  if (shouldReturnData) {
    const { data, error } = await query.select().single();
    if (error) throw error;
    return data;
  }

  const { error } = await query;
  if (error) throw error;
  return null;
};

export const updateCustomer = async (id: string, customerData: Partial<Omit<Customer, 'id'>>) => {
  const { data, error } = await supabase
    .from('customers')
    .update(customerData)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const deleteCustomer = async (id: string) => {
  const { error } = await supabase
    .from('customers')
    .delete()
    .eq('id', id);

  if (error) throw error;
  return true;
};

export const getCustomerByIdCard = async (idCard: string): Promise<Customer | null> => {
  const { data, error } = await supabase
    .from('customers')
    .select('*')
    .eq('id_card', parseInt(idCard)) // Convert string to number for query
    .maybeSingle();

  if (error) {
    console.error("Error fetching customer by ID card:", error);
    throw error;
  }
  return data;
};

export const getCustomers = async (limit?: number): Promise<Customer[]> => {
  try {
    let query = supabase
      .from('customers')
      .select('*')
      .order('name', { ascending: true });

    if (limit && limit > 0) {
      query = query.limit(limit);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching customers:', error);
      return [];
    }
    return data || [];
  } catch (error) {
    console.error('Unexpected error in getCustomers:', error);
    return [];
  }
};

// Plans
export const createPlan = async (planData: Omit<Plan, 'id' | 'created_at' | 'updated_at' | 'deleted_at'>) => {
  const { data, error } = await supabase
    .from('plans')
    .insert({
      name: planData.name,
      price: planData.price,
      time_duration: planData.time_duration,
      active: planData.active,
      branch_id: planData.branch_id,
    })
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const getPlans = async (branchId?: string): Promise<Plan[]> => {
  let query = supabase
    .from('plans')
    .select(`*, branch:branch_id(id, name)`)
    .order('name');

  if (branchId) {
    query = query.eq('branch_id', branchId);
  }

  const { data, error } = await query;

  if (error) throw error;
  return data || [];
};

export const updatePlan = async (id: string, planData: Partial<Plan>) => {
  const { data, error } = await supabase
    .from('plans')
    .update(planData)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const deletePlan = async (id: string) => {
  const { error } = await supabase
    .from('plans')
    .delete()
    .eq('id', id);

  if (error) throw error;
  return true;
};

// Rentals
export const createRental = async (rentalData: Omit<Rental, 'id' | 'customer' | 'bike' | 'manager' | 'branch' | 'plan'>) => {
  const startDateStr = typeof rentalData.start_date === 'object' ?
    rentalData.start_date.toISOString() : rentalData.start_date;

  const endDateStr = rentalData.end_date ?
    (typeof rentalData.end_date === 'object' ?
      rentalData.end_date.toISOString() : rentalData.end_date)
    : null;

  const expectedReturnTimeStr = rentalData.expected_return_time ?
    (typeof rentalData.expected_return_time === 'object' ?
      rentalData.expected_return_time.toISOString() : rentalData.expected_return_time)
    : null;

  const { data, error } = await supabase
    .from('rentals')
    .insert({
      customer_id: rentalData.customer_id,
      bike_id: rentalData.bike_id,
      manager_id: rentalData.manager_id,
      branch_id: rentalData.branch_id,
      plan_id: rentalData.plan_id,
      start_date: startDateStr,
      end_date: endDateStr,
      expected_return_time: expectedReturnTimeStr,
      duration_hours: rentalData.duration_hours,
      total_amount: rentalData.total_amount,
      status: rentalData.status
    })
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const getRentals = async (startDate?: string, dateComparison: 'gte' | 'eq' = 'gte'): Promise<RentalWithRelations[]> => {
  let query = supabase
    .from('rentals')
    .select(`
      *,
      customer:customer_id(id, name, phone),
      bike:bike_id(id, model, type, color, number, size),
      manager:manager_id(id, full_name),
      branch:branch_id(id, name, address, operator_percentage, administrator_percentage),
      plan:plan_id(id, name, price, time_duration)
    `)
    .order('start_date', { ascending: false });

  if (startDate) {
    if (dateComparison === 'eq') {
      // Parse the date string as local time
      const localStartDate = new Date(`${startDate}T00:00:00`);
      const localEndDate = new Date(localStartDate);
      localEndDate.setHours(23, 59, 59, 999);

      query = query
        .gte('start_date', localStartDate.toISOString())
        .lte('start_date', localEndDate.toISOString());
    } else if (dateComparison === 'gte') {
      query = query.gte('start_date', startDate);
    }
  }

  const { data, error } = await query;

  if (error) throw error;
  return data || [];
};

export const getCompletedRentalsForOperator = async (operatorId: string, startTime: string): Promise<RentalWithRelations[]> => {
  const { data, error } = await supabase
    .from('rentals')
    .select(`
      *,
      customer:customers(id, name, phone),
      bike:bikes(id, model, type, color, number),
      manager:profiles(id, full_name),
      branch:branches(id, name, address),
      plan:plans(id, name, price, time_duration)
    `)
    .eq('manager_id', operatorId)
    .eq('status', 'completed')
    .gte('end_date', startTime)
    .order('end_date', { ascending: false });

  if (error) throw error;
  return data || [];
};

export const getActiveRentals = async (startDate?: string): Promise<RentalWithRelations[]> => {
  let query = supabase
    .from('rentals')
    .select(`
      *,
      customer:customer_id(id, name, phone),
      bike:bike_id(id, model, type, color, number),
      manager:manager_id(id, full_name),
      branch:branch_id(id, name, address),
      plan:plan_id(id, name, price, time_duration)
    `)
    .eq('status', 'active')
    .order('start_date', { ascending: false });

  if (startDate) {
    query = query.gte('start_date', startDate);
  }

  const { data, error } = await query;

  if (error) throw error;
  return data || [];
};

export const getActiveRentalsLigth = async (operatorId?: string, startDate?: string): Promise<RentalWithRelations[]> => {
  let query = supabase
    .from('rentals')
    .select(`
      *,
      customer:customer_id(id, name, phone),
      bike:bike_id(id, model, type, color, number)
    `)
    .eq('status', 'active')
    .eq('manager_id', operatorId)

  if (startDate) {
    query = query.gte('start_date', startDate);
  }

  const { data, error } = await query;

  if (error) throw error;
  return data || [];
};

// Branches
export const getBranches = async (): Promise<Branch[]> => {
  const { data, error } = await supabase
    .from('branches')
    .select('*')
    .order('name');

  if (error) throw error;
  return data;
};

export const getBranchName = async (branchId: string): Promise<{ name: string } | null> => {
  const { data, error } = await supabase
    .from('branches')
    .select('name')
    .eq('id', branchId)
    .single();

  if (error) throw error;
  return data;
};

export const updateBranch = async (id: string, branchData: Partial<Branch>) => {
  const { data, error } = await supabase
    .from('branches')
    .update(branchData)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const deleteBranch = async (id: string) => {
  const { error } = await supabase
    .from('branches')
    .delete()
    .eq('id', id);

  if (error) throw error;
  return true;
};

// Bikes
export const createBike = async (bikeData: Omit<Bike, 'id'>) => {
  const { data, error } = await supabase
    .from('bikes')
    .insert({
      number: bikeData.number, // Now correctly as number
      type: bikeData.type,
      model: bikeData.model,
      color: bikeData.color,
      photo_url: bikeData.photo_url,
      branch_id: bikeData.branch_id, // Add branch_id
      created_at: new Date().toISOString(),
    })
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const getBikes = async (): Promise<Bike[]> => {
  const { data: allBikes, error: bikesError } = await supabase
    .from('bikes')
    .select('*')
    .order('model').order('number');

  if (bikesError) {
    console.error("Error fetching bikes:", bikesError);
    throw bikesError;
  }

  if (!allBikes) {
    return [];
  }

  const { data: activeRentals, error: rentalsError } = await supabase
    .from('rentals')
    .select('bike_id')
    .eq('status', 'active');

  if (rentalsError) {
    console.error("Error fetching active rentals:", rentalsError);
    throw rentalsError;
  }

  const rentedBikeIds = new Set(activeRentals?.map(rental => rental.bike_id) || []);
  const availableBikes = allBikes.filter(bike => !rentedBikeIds.has(bike.id));

  return availableBikes;
};

export const updateBike = async (id: string, bikeData: Partial<Bike>) => {
  const { data, error } = await supabase
    .from('bikes')
    .update(bikeData)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data;
};

// Delete a bike
export const deleteBike = async (id: string) => {
  const { error } = await supabase
    .from('bikes')
    .delete()
    .eq('id', id);

  if (error) throw error;
  return true;
};

// Fetch bikes from the database
export const getAllBikes = async (userProfile: UserProfile | null): Promise<Bike[]> => {
  let query = supabase
    .from('bikes')
    .select('*, branch:branch_id(id, name)')
    .order('number');

  if (userProfile && userProfile.role === 'branch_admin' && userProfile.branch_id) {
    query = query.eq('branch_id', userProfile.branch_id);
  }
  // For super_admin, no additional filtering is needed.

  const { data, error } = await query;

  if (error) throw error;
  return data as Bike[];
};

// Upload files
export const uploadFile = async (file: string, filePath: string) => {
  // Compress image before uploading to reduce file size drastically
  const compressed = await compressImage(file);
  const base64Data = compressed.includes(',') ? compressed.split(',')[1] : compressed;

  const byteCharacters = atob(base64Data);
  const byteArrays = [];
  for (let i = 0; i < byteCharacters.length; i++) {
    byteArrays.push(byteCharacters.charCodeAt(i));
  }
  const byteArray = new Uint8Array(byteArrays);

  const { data, error } = await supabase
    .storage
    .from('rental_photos')
    .upload(filePath, byteArray, {
      contentType: 'image/jpeg',
      upsert: true
    });

  if (error) throw error;

  const { data: { publicUrl } } = supabase
    .storage
    .from('rental_photos')
    .getPublicUrl(data.path);

  return publicUrl;
};

export const uploadContractPdf = async (pdfBlob: Blob, filePath: string): Promise<string> => {
  const { data, error } = await supabase
    .storage
    .from('contracts')
    .upload(filePath, pdfBlob, {
      contentType: 'application/pdf',
      upsert: true
    });

  if (error) throw error;

  const { data: { publicUrl } } = supabase
    .storage
    .from('contracts')
    .getPublicUrl(data.path);

  return publicUrl;
};


export const completeRental = async (rentalId: string) => {
  const { data, error } = await supabase
    .from('rentals')
    .update({
      status: 'completed',
      end_date: new Date().toISOString()
    })
    .eq('id', rentalId)
    .select()
    .single();

  if (error) throw error;
  return data;
};

// Shifts services
export interface GroupedShifts {
  branchId: string;
  branchName: string;
  shifts: ShiftWithRelations[];
}

export const getShifts = async (userProfile: UserProfile | null): Promise<ShiftWithRelations[] | GroupedShifts[]> => {
  if (!userProfile) {
    // Or handle as an error, depending on application flow
    return [];
  }

  let query = supabase
    .from('shifts')
    .select(`
      id,
      start_time,
      end_time,
      total_collected,
      total_rentals,
      completed_rentals,
      fully_paid,
      created_at,
      updated_at,
      configuracion_id,
      operator_amount,
      amount_admin,
      operator_percentage,
      administrator_percentage,
      operator_id,
      status,
      is_deleted,
      deleted_at,
      operator:profiles (
        id,
        full_name,
        avatar_url,
        role,
        branch_id,
        branch:branch_id (
          id,
          name,
          operator_percentage,
          administrator_percentage
        )
      ),
      payments:shift_payments (
        id,
        bank_id,
        amount,
        reference,
        exchange_rate,
        receipt_photo_url,
        created_at,
        status,
        bank:bank_id ( id, name )
      )
    `)
    .eq('is_deleted', false)
    .is('deleted_at', null)
    .order('start_time', { ascending: false });

  if (userProfile.role === 'operator') {
    query = query.eq('operator_id', userProfile.id);
    const { data, error } = await query;
    if (error) throw error;
    const operatorShifts = (data as ShiftWithRelations[]) || [];
    return operatorShifts.filter(s => !s.is_deleted && !s.deleted_at);
  } else if (userProfile.role === 'super_admin' || userProfile.role === 'branch_admin') {
    const { data: allShifts, error } = await query;
    if (error) throw error;

    if (!allShifts) return [];

    const activeShifts = allShifts.filter((s: any) => !s.is_deleted && !s.deleted_at);

    // Enrich shifts with direct branch info for easier access
    const enrichedShifts = activeShifts.map(s => {
      const shift = s as any; // Temp type assertion
      return {
        ...shift,
        branch: shift.operator?.branch
          ? { id: shift.operator.branch.id, name: shift.operator.branch.name }
          : null,
      } as ShiftWithRelations;
    });

    // Group shifts by branch
    const groupedByBranch: Record<string, GroupedShifts> = {};
    enrichedShifts.forEach(shift => {
      const branchId = shift.operator?.branch?.id || 'unknown';
      const branchName = shift.operator?.branch?.name || 'Sucursal Desconocida';

      if (!groupedByBranch[branchId]) {
        groupedByBranch[branchId] = { branchId, branchName, shifts: [] };
      }
      groupedByBranch[branchId].shifts.push(shift);
    });

    // Sort shifts within each branch
    for (const branchId in groupedByBranch) {
      groupedByBranch[branchId].shifts.sort((a, b) => {
        // Sort by status: 1. Pending Payment, 2. Active, 3. Paid
        const getStatusOrder = (s: ShiftWithRelations) => {
          if ((s.end_time && !s.fully_paid) || s.status === 'in_review') return 1; // Pending Payment
          if (!s.end_time) return 2; // Active
          if (s.end_time && s.fully_paid) return 3; // Paid
          return 4; // Should not happen
        };

        const statusOrderA = getStatusOrder(a);
        const statusOrderB = getStatusOrder(b);

        if (statusOrderA !== statusOrderB) {
          return statusOrderA - statusOrderB;
        }
        // If status is the same, sort by start_time descending (newest first)
        return new Date(b.start_time).getTime() - new Date(a.start_time).getTime();
      });
    }
    return Object.values(groupedByBranch);
  }

  return []; // Default empty for other roles or if profile is missing
};

export const getCurrentShift = async (operatorId: string): Promise<Shift | null> => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const { data, error } = await supabase
    .from('shifts')
    .select('*')
    .eq('operator_id', operatorId)
    .gte('created_at', today.toISOString())
    .lt('created_at', tomorrow.toISOString())
    .is('end_time', null)
    .eq('is_deleted', false)
    .is('deleted_at', null)
    .single();

  if (error && error.code !== 'PGRST116') throw error;
  return data;
};

export const createShift = async (operatorId: string): Promise<Shift> => {
  // First get the operator's profile and branch details
  const { data: profile, error: profileError } = await supabase
    .from('profiles')
    .select(`
      id,
      branch:branch_id (
        id,
        operator_percentage,
        administrator_percentage
      )
    `)
    .eq('id', operatorId)
    .single();

  let operatorPercentage: number | null = null;
  let administratorPercentage: number | null = null;

  if (!profileError && profile?.branch) {
    const branch = profile.branch as any;
    if (branch.operator_percentage !== null && branch.operator_percentage !== undefined) {
      operatorPercentage = branch.operator_percentage;
      administratorPercentage = branch.administrator_percentage;
    }
  }

  // Get the active configuration as fallback or for configuration_id
  const { data: configuration, error: configError } = await supabase
    .from('configuration')
    .select('id, operator_percentage, administrator_percentage')
    .eq('active', true)
    .single();

  if (configError || !configuration) {
    throw new Error('No se encontró configuración activa');
  }

  if (operatorPercentage === null || administratorPercentage === null) {
    operatorPercentage = configuration.operator_percentage;
    administratorPercentage = configuration.administrator_percentage;
  }

  const { data, error } = await supabase
    .from('shifts')
    .insert({
      operator_id: operatorId,
      start_time: new Date().toISOString(),
      configuracion_id: configuration.id,
      operator_percentage: operatorPercentage,
      administrator_percentage: administratorPercentage,
    })
    .select()
    .single();

  if (error) {
    throw error;
  }

  return data;
};

export const updateShift = async (shiftId: string, totalCollected: number, totalRented: number): Promise<Shift> => {
  // Get frozen percentages from the shift itself
  const { data: shiftData, error: shiftError } = await supabase
    .from('shifts')
    .select('operator_percentage, administrator_percentage, operator_id')
    .eq('id', shiftId)
    .single();

  if (shiftError || !shiftData) {
    throw new Error('No se encontró la jornada');
  }

  let opPercentage = shiftData.operator_percentage;
  let adminPercentage = shiftData.administrator_percentage;

  // Fallback if they are not frozen (for older shifts)
  if (opPercentage === null || opPercentage === undefined || adminPercentage === null || adminPercentage === undefined) {
    // Perform hierarchy lookup
    const { data: profile } = await supabase
      .from('profiles')
      .select('branch:branch_id (operator_percentage, administrator_percentage)')
      .eq('id', shiftData.operator_id)
      .single();
    
    const branch = profile?.branch as any;
    if (branch && branch.operator_percentage !== null && branch.operator_percentage !== undefined) {
      opPercentage = branch.operator_percentage;
      adminPercentage = branch.administrator_percentage;
    } else {
      const { data: configuration } = await supabase
        .from('configuration')
        .select('operator_percentage, administrator_percentage')
        .eq('active', true)
        .single();
      
      opPercentage = configuration?.operator_percentage ?? 50;
      adminPercentage = configuration?.administrator_percentage ?? 50;
    }
  }

  const operatorAmount = (totalCollected * opPercentage) / 100;
  const adminAmount = (totalCollected * adminPercentage) / 100;

  const { data, error } = await supabase
    .from('shifts')
    .update({
      total_collected: totalCollected,
      operator_amount: operatorAmount,
      amount_admin: adminAmount,
      total_rentals: totalRented,
    })
    .eq('id', shiftId)
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const markShiftAsPaid = async (shiftId: string): Promise<Shift> => {
  const { data, error } = await supabase
    .from('shifts')
    .update({ fully_paid: true, status: 'paid' })
    .eq('id', shiftId)
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const incrementShiftTotals = async (shiftId: string, amountToAdd: number, rentalsToAdd: number) => {
  const { error } = await supabase.rpc('increment_shift_totals', {
    p_shift_id: shiftId,
    p_amount_to_add: amountToAdd,
    p_rentals_to_add: rentalsToAdd,
  });

  if (error) {
    console.error('Error incrementing shift totals:', error);
    throw error;
  }
};

export const getConfiguration = async (): Promise<Configuration> => {
  const { data, error } = await supabase
    .from('configuration')
    .select('*')
    .eq('active', true)
    .single();

  if (error) throw error;
  return data;
};

export const updateConfiguration = async (config: Partial<Configuration>): Promise<Configuration> => {
  const { data, error } = await supabase
    .from('configuration')
    .update(config)
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const sendWhatsappMessage = async ({ cardID, message, template, variables, contract, contractUrl }: {
  cardID: number,
  message?: string,
  template?: string,
  variables?: string[],
  contract?: boolean,
  contractUrl?: string
}): Promise<void> => {
  const { data: parameter, error: paramError } = await supabase
    .from('parameters')
    .select('parameter, value')
    .eq('parameter', 'WhatsAppActive')
    .maybeSingle();

  if (paramError) {
    console.error("Error fetching WhatsAppActive parameter:", paramError);
    return;
  }

  if (!parameter || parameter.value == '0' || parameter.value == 'false') {
    console.warn(
      "[sendWhatsappMessage] Envío omitido: el parámetro 'WhatsAppActive' " +
        (!parameter
          ? "no existe en la tabla 'parameters' (verifica si RLS bloquea el SELECT para authenticated)."
          : `está desactivado (value='${parameter.value}').`)
    );
    return;
  }

  const { data, error } = await supabase.functions.invoke('twilioSend', {
    body: {
      to: cardID,
      message,
      template,
      variables,
      contract,
      contractUrl
    },
  });

  if (error) throw error;
  return data;
};